import { requiredEnv } from './requiredEnv';

/**
 * Memory Capacity API client
 * Handles memory status fetching and slot purchases
 */

const BACKEND_URL = requiredEnv('REACT_APP_BACKEND_URL');

/**
 * @typedef {Object} MemoryStatus
 * @property {number} current_count - Current number of memories
 * @property {number} free_limit - Free memory limit
 * @property {number} purchased_slots - Number of purchased slots
 * @property {number} total_capacity - Total capacity (free + purchased)
 * @property {boolean} can_create - Whether user can create more memories
 * @property {boolean} [is_admin] - True when UID is in server admin list (unlimited slots)
 */

/**
 * Get user's memory capacity status
 * @param {Object} user - Firebase user object
 * @returns {Promise<MemoryStatus>}
 */
export async function getMemoryStatus(user) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/memories/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch memory status: ${response.status}`);
  }

  return await response.json();
}

/**
 * @typedef {Object} PurchaseResult
 * @property {boolean} success - Whether purchase succeeded
 * @property {number} new_purchased_slots - New total purchased slots
 * @property {number} new_credit_balance - New credit balance
 */

/**
 * Purchase additional memory slots
 * @param {Object} user - Firebase user object
 * @param {number} slots - Number of slots to purchase
 * @param {number} credits - Credit cost
 * @returns {Promise<PurchaseResult>}
 */
export async function purchaseMemorySlots(user, slots, credits) {
  const idToken = await user.getIdToken();

  const response = await fetch(`${BACKEND_URL}/memories/purchase-slots`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ slots, credits }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to purchase slots: ${response.status}`);
  }

  return await response.json();
}
